/**
 * RiskMatrixBlock - Display a risk assessment matrix
 */

import React from 'react'
import { RiskMatrixBlock as RiskMatrixBlockType } from '../../../../lib/cockpit/schemas'

interface RiskMatrixBlockProps {
  data: RiskMatrixBlockType['data']
  blockId: string
  compact?: boolean
}

const LEVEL_COLORS = {
  very_low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  very_high: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
}

function RiskMatrixBlock({ data, blockId: _blockId, compact }: RiskMatrixBlockProps) {
  const { risks } = data

  if (!risks || risks.length === 0) {
    return (
      <div className="text-gray-500 dark:text-gray-400 text-sm italic">
        No risks identified
      </div>
    )
  }

  if (compact) {
    return (
      <div className="flex flex-wrap gap-2">
        {risks.slice(0, 5).map((risk, idx) => (
          <span
            key={risk.id || idx}
            className={`px-2 py-1 rounded text-xs font-medium ${
              LEVEL_COLORS[risk.likelihood || 'medium']
            }`}
          >
            {risk.name}
          </span>
        ))}
        {risks.length > 5 && (
          <span className="px-2 py-1 text-xs text-gray-500">+{risks.length - 5} more</span>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {risks.map((risk, idx) => (
        <div
          key={risk.id || idx}
          className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h4 className="font-medium text-gray-900 dark:text-white">{risk.name}</h4>
              {risk.description && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{risk.description}</p>
              )}
              {risk.mitigation && (
                <p className="mt-2 text-sm text-green-600 dark:text-green-400">
                  <span className="font-medium">Mitigation:</span> {risk.mitigation}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1 items-end text-xs">
              {risk.likelihood && (
                <span className={`px-2 py-1 rounded font-medium ${LEVEL_COLORS[risk.likelihood]}`}>
                  Likelihood: {typeof risk.likelihood === 'string' ? risk.likelihood.replace('_', ' ') : String(risk.likelihood)}
                </span>
              )}
              {risk.impact && (
                <span className={`px-2 py-1 rounded font-medium ${LEVEL_COLORS[risk.impact]}`}>
                  Impact: {typeof risk.impact === 'string' ? risk.impact.replace('_', ' ') : String(risk.impact)}
                </span>
              )}
            </div>
          </div>
          {risk.category && (
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
              Category: {risk.category}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default RiskMatrixBlock

